import { useId, useState } from "react";
import type { FormEvent } from "react";
import { useMemos } from "@/hooks/useMemos";
import { StateMessage } from "@/components/StateMessage";
import { problemMessage } from "@/api/client";
import type { Guid } from "@/api/types";
import "./MemosCard.css";

/**
 * Home's new "Memos" card — ef-051-1: an inline add form (an optional date, a body up
 * to 500 characters) and the tenant's own running notes, owner-only. Every figure/row is
 * the API's own (`GET /api/demo/home/memos?limit=5`, `useMemos`); nothing here re-sorts
 * or re-filters — ordering (dated-ascending, undated last) is the server's contract.
 *
 * `role="region"` + `aria-label` (not `aria-labelledby`) and `data-state` on the SAME
 * element — same requirement `CashFlowPlannerCard.tsx`'s own doc states for itself: both
 * the dotnet-independent E2E spec (`home-memos.spec.ts`) and `rules/ui-breadcrumbs.md`
 * §1's role-based-locator rule read this element directly, not a descendant.
 *
 * ONE PRIMARY ACTION PER ROW (ADR 0009 §4) — a row carries a single "Archive" button,
 * never a disclosure of several. No edit-in-place UI this slice (`PATCH` exists
 * server-side for a future screen, but the build brief's own file scope draws the Home
 * card's UI at add + archive).
 *
 * NO ENTITY PICKER — the inline form never sends `entityId` (build brief; `entity_id`
 * exists on the row for a future screen that files a memo against a specific farm).
 */
export function MemosCard() {
  const { memos, status, error, createMemo, archiveMemo } = useMemos();
  const isEmpty = status === "ready" && (memos?.length ?? 0) === 0;
  const dataState = isEmpty ? "empty" : status;

  const noteId = useId();
  const dateId = useId();

  const [note, setNote] = useState("");
  const [memoDate, setMemoDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [archivingId, setArchivingId] = useState<Guid | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    const trimmed = note.trim();
    if (trimmed === "") {
      setFormError("Enter a note.");
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      await createMemo({ body: trimmed, memoDate: memoDate === "" ? undefined : memoDate });
      setNote("");
      setMemoDate("");
    } catch (err) {
      // Client-side ONLY the empty-note check above — MemoEndpoints.cs's own
      // ValidateBody/ValidateMemoDate checks still run server-side and remain the actual
      // contract (the 500-char bound, the "at most one day ahead" window). Never guessed
      // at client-side; surfaced inline from the server's own response.
      setFormError(problemMessage(err, "Could not add this memo."));
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(id: Guid): Promise<void> {
    setArchivingId(id);
    setRowError(null);
    try {
      await archiveMemo(id);
    } catch (err) {
      setRowError(problemMessage(err, "Could not archive this memo."));
    } finally {
      setArchivingId(null);
    }
  }

  return (
    <section className="memos-card" role="region" aria-label="Memos" data-state={dataState}>
      <div className="memos-card__head">
        <h2 className="memos-card__title">Memos</h2>
      </div>

      <form className="memos-card__form" onSubmit={handleSubmit}>
        <div className="memos-card__field memos-card__field--note">
          <label htmlFor={noteId}>Note</label>
          <input
            id={noteId}
            type="text"
            value={note}
            maxLength={500}
            placeholder="Check on the north fence line"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <div className="memos-card__field memos-card__field--date">
          <label htmlFor={dateId}>Date (optional)</label>
          <input
            id={dateId}
            type="date"
            value={memoDate}
            onChange={(e) => setMemoDate(e.target.value)}
          />
        </div>
        <button type="submit" className="btn btn--primary" disabled={saving}>
          Add memo
        </button>
      </form>

      {formError && (
        <p className="memos-card__form-error" role="alert">
          {formError}
        </p>
      )}

      {status === "loading" && <StateMessage tone="loading" title="Loading memos…" />}

      {status === "error" && (
        <StateMessage tone="error" title="Could not load memos." detail={error ?? undefined} />
      )}

      {isEmpty && (
        <StateMessage
          tone="empty"
          title="No memos yet."
          detail="Add a running note above to get started."
        />
      )}

      {rowError && (
        <p className="memos-card__form-error" role="alert">
          {rowError}
        </p>
      )}

      {status === "ready" && memos && memos.length > 0 && (
        <ul className="memos-card__list">
          {memos.map((memo) => (
            <li key={memo.id} className="memos-card__row">
              <span className="memos-card__meta">
                {memo.memoDate && <span className="memos-card__date">{memo.memoDate}</span>}
                <span className="memos-card__body">{memo.body}</span>
              </span>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => void handleArchive(memo.id)}
                disabled={archivingId === memo.id}
              >
                Archive
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
